import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button, Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Select, { type MultiValue } from 'react-select';
import type { ClientType } from '@econtractor/types';
import * as apiClient from 'api/apiClients';
import { useTheme } from 'context/ThemeContext';
import { selectStylesDark, selectStylesLigth } from 'utils/helpers/structs/selectStyles';
import { TeamSelect } from 'modules/user';
import { ClientSelected } from 'modules/client';
import { defaultObjectStatuses, useObject } from '../context/ObjectProvider';
import vconst from 'utils/helpers/structs/vconst';
import { isSameIdSet } from 'utils/helpers/list/isSameIdSet';

type StatusOption = {
    value: number;
    label: string;
};

const statusIcons: Record<string, ReactNode> = {
    '1': <i className="bi bi-play-circle fs-14 text-success" />,
    '0': <i className="bi bi-pause-circle fs-14 text-warning" />,
    '99': <i className="bi bi-check-circle fs-14 text-success" />,
    '100': <i className="bi bi-check-circle-fill fs-14 text-success" />,
    '-1': <i className="bi bi-x-circle-fill fs-14 text-danger" />,
};

const ObjectsFilter = () => {
    const { t } = useTranslation(['Objects', 'common']);
    const { darkTheme } = useTheme();
    const {
        filterStatus,
        setFilterStatus,
        filterTeamId,
        setFilterTeamId,
        filterClientId,
        setFilterClientId,
    } = useObject();
    const [filterClient, setFilterClient] = useState<ClientType | null>(null);
    const [show, setShow] = useState(false);
    const rootRef = useRef<HTMLSpanElement | null>(null);
    const selectTheme = darkTheme ? selectStylesDark : selectStylesLigth;

    const filterActive =
        !isSameIdSet(filterStatus, defaultObjectStatuses)
        || Boolean(filterTeamId)
        || Boolean(filterClientId);

    const statusOptions = useMemo<StatusOption[]>(
        () =>
            vconst.objectAllStatuses.map((oStatus: number) => ({
                value: oStatus,
                label: t(`status.${oStatus}`),
            })),
        [t],
    );

    const selectedStatuses = useMemo(
        () => statusOptions.filter((opt) => filterStatus.includes(opt.value)),
        [filterStatus, statusOptions],
    );

    useEffect(() => {
        if (!filterClientId) {
            setFilterClient(null);
            return;
        }
        if (filterClient?.ID === filterClientId) return;

        let cancelled = false;
        (async () => {
            const resClient = await apiClient.getClientById(filterClientId);
            if (cancelled) return;
            setFilterClient(resClient || null);
            if (!resClient) setFilterClientId(null);
        })();

        return () => {
            cancelled = true;
        };
    }, [filterClientId]);

    useEffect(() => {
        if (!show) return;

        const onPointerDown = (event: globalThis.MouseEvent) => {
            const target = event.target as HTMLElement | null;
            if (!target) return;
            if (rootRef.current?.contains(target)) return;
            if (
                target.closest('.object-filter-menu')
                || target.closest('[class*="-menu"]')
                || target.closest('.user-selected')
            ) {
                return;
            }
            setShow(false);
        };

        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [show]);

    const handleChangeStatus = (options: MultiValue<StatusOption>) => {
        setFilterStatus((options || []).map((opt) => opt.value));
    };

    const handleChangeClient = (client: ClientType | null) => {
        setFilterClient(client);
        setFilterClientId(client?.ID ? Number(client.ID) : null);
    };

    const handleResetFilters = () => {
        setFilterStatus(defaultObjectStatuses);
        setFilterTeamId(null);
        setFilterClient(null);
        setFilterClientId(null);
    };

    const formatStatusOption = (option: StatusOption, { context }: { context: 'menu' | 'value' }) => {
        if (context === 'value') return option.label;
        return (
            <span className="d-inline-flex align-items-center gap-1">
                {statusIcons?.[String(option.value)]}
                <span>{option.label}</span>
            </span>
        );
    };

    const menu = (
        <Dropdown.Menu
            className="object-filter-menu p-3"
            renderOnMount
            popperConfig={{
                strategy: 'fixed',
                modifiers: [
                    {
                        name: 'preventOverflow',
                        options: { boundary: 'viewport', padding: 12 },
                    },
                    {
                        name: 'flip',
                        options: { fallbackPlacements: ['bottom-end', 'top-end', 'bottom-start'] },
                    },
                ],
            }}
            onClick={(e) => e.stopPropagation()}
            onWheel={(e) => e.stopPropagation()}
            onMouseDown={(e) => {
                const target = e.target as HTMLElement | null;
                if (
                    target?.closest('[class*="-clear-indicator"]')
                    || target?.closest('[class*="-multi-value__remove"]')
                    || target?.closest('.user-selected')
                ) {
                    e.stopPropagation();
                }
            }}
        >
            <div className="mb-3">
                <label className="form-label text-muted fs-12 mb-1">{t('filterStatus')}</label>
                <Select<StatusOption, true>
                    isMulti
                    isClearable
                    isSearchable={false}
                    options={statusOptions}
                    value={selectedStatuses}
                    onChange={handleChangeStatus}
                    placeholder={t('filterStatus')}
                    className="w-100 object-filter-select"
                    classNamePrefix="object-filter-select"
                    styles={{
                        ...selectTheme,
                        menuPortal: (base) => ({
                            ...base,
                            zIndex: 2100,
                        }),
                        multiValue: (base) => ({
                            ...(selectTheme.multiValue ? selectTheme.multiValue(base) : base),
                            borderRadius: '6px',
                            padding: 0,
                            margin: '1px 2px',
                            minHeight: 0,
                        }),
                        multiValueLabel: (base) => ({
                            ...(selectTheme.multiValueLabel
                                ? selectTheme.multiValueLabel(base)
                                : base),
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            padding: '2px 6px',
                            lineHeight: 1.2,
                        }),
                        multiValueRemove: (base) => ({
                            ...(selectTheme.multiValueRemove
                                ? selectTheme.multiValueRemove(base)
                                : base),
                            padding: '0 4px',
                            borderRadius: '0 6px 6px 0',
                        }),
                    }}
                    formatOptionLabel={formatStatusOption}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition="fixed"
                    menuShouldScrollIntoView={false}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                />
            </div>

            <div className="mb-3">
                <label className="form-label text-muted fs-12 mb-1">{t('filterTeam')}</label>
                <TeamSelect
                    hideLabel
                    isClearable
                    showEmpty={false}
                    placeholder={t('filterTeam')}
                    value={filterTeamId || null}
                    onChange={(opt: { value?: number } | null) => {
                        setFilterTeamId(opt?.value ? Number(opt.value) : null);
                    }}
                />
            </div>

            <div className="mb-3">
                <ClientSelected
                    title={t('filterClient')}
                    className="mb-0"
                    allowNew={false}
                    clearable
                    maxHeight={160}
                    value={filterClient}
                    onChange={handleChangeClient}
                />
            </div>

            <div className="text-end">
                <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="mb-0 min70px"
                    style={{ height: 24 }}
                    disabled={!filterActive}
                    onClick={handleResetFilters}
                >
                    {t('common:buttons.reset')}
                </Button>
                <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    className="mb-0 ms-2 min70px"
                    style={{ height: 24 }}
                    onClick={() => setShow(false)}
                >
                    {t('common:buttons.close')}
                </Button>
            </div>
        </Dropdown.Menu>
    );

    return (
        <span ref={rootRef} className="d-inline-block">
            <Dropdown
                align="end"
                autoClose={false}
                show={show}
                onToggle={(next, meta) => {
                    if (next) {
                        setShow(true);
                        return;
                    }
                    if (meta?.source === 'click') setShow(false);
                }}
            >
                <Dropdown.Toggle
                    className="fs-14 px-2 ms-1"
                    size="sm"
                    variant={filterActive ? 'danger' : 'primary'}
                    id="dropdown-objects-filter"
                    title={t('toolObjectFilter')}
                >
                    <i className="bi bi-funnel fs-14 p-0" />
                </Dropdown.Toggle>

                {typeof document !== 'undefined' ? createPortal(menu, document.body) : menu}
            </Dropdown>
        </span>
    );
};

export default ObjectsFilter;
